import { useCallback } from 'react'
import { useDropzone } from 'react-dropzone'
import { useTranslation } from '../../contexts/LanguageContext'

interface PdfUploaderProps {
  onFileSelect: (file: File) => void
  disabled?: boolean
}

export function PdfUploader({ onFileSelect, disabled }: PdfUploaderProps) {
  const { t } = useTranslation()

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        onFileSelect(acceptedFiles[0])
      }
    },
    [onFileSelect]
  )

  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
    },
    maxFiles: 1,
    maxSize: 10 * 1024 * 1024, // 10MB
    disabled,
  })

  return (
    <div
      {...getRootProps()}
      className={`relative border-2 border-dashed rounded-2xl p-6 sm:p-10 text-center transition-all duration-200 cursor-pointer ${
        isDragActive && !isDragReject
          ? 'border-primary bg-primary/5'
          : isDragReject
            ? 'border-red-400 bg-red-50'
            : disabled
              ? 'border-gray-200 bg-gray-50 cursor-not-allowed'
              : 'border-gray-200 hover:border-primary/40 hover:bg-primary/[0.02]'
      }`}
    >
      <input {...getInputProps()} />

      {/* Icon */}
      <div
        className={`w-12 h-12 mx-auto mb-3 rounded-2xl flex items-center justify-center ${
          isDragActive && !isDragReject
            ? 'bg-primary/10'
            : isDragReject
              ? 'bg-red-100'
              : 'bg-gray-100'
        }`}
      >
        {isDragReject ? (
          <svg className="w-6 h-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        ) : (
          <svg
            className={`w-6 h-6 ${isDragActive ? 'text-primary' : 'text-gray-400'}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.5}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
          </svg>
        )}
      </div>

      {/* Text */}
      {isDragReject ? (
        <>
          <p className="text-red-600 font-semibold text-sm">{t.import.invalidFileType}</p>
          <p className="text-xs text-red-500 mt-1">{t.import.onlyPdf}</p>
        </>
      ) : isDragActive ? (
        <>
          <p className="text-primary font-semibold text-sm">{t.import.dropHere}</p>
          <p className="text-xs text-primary/70 mt-1">{t.import.dropToImport}</p>
        </>
      ) : (
        <>
          <p className="text-gray-900 font-semibold text-sm">
            <span className="hidden sm:inline">{t.import.dragOrClick}</span>
            <span className="sm:hidden">{t.import.tapToSelect}</span>
          </p>
          <p className="text-xs text-gray-500 mt-1">{t.import.pdfUpTo}</p>
        </>
      )}

      {/* File type badge */}
      <div className="mt-5 flex items-center justify-center">
        <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-gray-50 border border-gray-100 rounded-lg text-xs font-medium text-gray-500">
          <svg className="w-3.5 h-3.5 text-red-400" viewBox="0 0 24 24" fill="currentColor">
            <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm-1 2l5 5h-5V4zm-2.5 9.5L14 17H9l2.5-3.5zm0 0L9 11l2.5 2.5z" />
          </svg>
          PDF
        </span>
      </div>
    </div>
  )
}
